const DEFAULT_MAX_FULFILLMENT_ITEM_PHOTO_SIZE_MB = 10

const fulfillmentItemPhotoMaxSizeMbFromEnv = Number(import.meta.env?.VITE_MAX_FULFILLMENT_ITEM_PHOTO_SIZE_MB)

const resolvedMaxFulfillmentItemPhotoSizeMb =
  Number.isFinite(fulfillmentItemPhotoMaxSizeMbFromEnv) && fulfillmentItemPhotoMaxSizeMbFromEnv > 0
    ? fulfillmentItemPhotoMaxSizeMbFromEnv
    : DEFAULT_MAX_FULFILLMENT_ITEM_PHOTO_SIZE_MB

/**
 * Represents the environment configuration object.
 * This object is used to validate and access environment variables.
 */
const env = {
  /**
   * The current environment mode.
   * @remarks
   * Possible values are 'development', 'production', 'test', or 'local'.
   * This variable determines the mode in which the application is running.
   */
  NODE_ENV: import.meta.env?.VITE_NODE_ENV,

  /**
   * Use Mock Data
   * @remarks
   * This variable determines whether to use mock data.
   * Possible values are 'true' or 'false'.
   */
  USE_MOCK: import.meta.env?.VITE_USE_MOCK,

  /**
   * Support Email Address
   * @example "support@appshop.biz"
   * This email address is used for customer support.
   */
  SUPPORT_EMAIL_ADDRESS: import.meta.env?.VITE_SUPPORT_EMAIL_ADDRESS,

  /**
   * Business Name
   * @example "Hourrier, Inc."
   * This name is used in the footer.
   */
  BUSINESS_NAME: import.meta.env?.VITE_BUSINESS_NAME,

  /**
   * Business Phone Number
   * @example "1234567890"
   * This phone number is used in the footer and in the policy pages.
   */
  BUSINESS_PHONE_NUMBER: import.meta.env?.VITE_BUSINESS_PHONE_NUMBER,

  /**
   * Firebase API Key
   * @example "your_firebase_api_key"
   * This key is used to authenticate requests from your app to Firebase services.
   */
  FIREBASE_API_KEY: import.meta.env?.VITE_FIREBASE_API_KEY,

  /**
   * Firebase Auth Domain
   * @example "your_firebase_auth_domain"
   * This domain is used for Firebase Authentication.
   */
  FIREBASE_AUTH_DOMAIN: import.meta.env?.VITE_FIREBASE_AUTH_DOMAIN,

  /**
   * Firebase Project ID
   * @example "your_firebase_project_id"
   * This ID uniquely identifies your Firebase project.
   */
  FIREBASE_PROJECT_ID: import.meta.env?.VITE_FIREBASE_PROJECT_ID,

  /**
   * Firebase Storage Bucket
   * @example "your_firebase_storage_bucket"
   * This bucket is used to store files in Firebase Storage.
   */
  FIREBASE_STORAGE_BUCKET: import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET,

  /**
   * Firebase Messaging Sender ID
   * @example "your_firebase_messaging_sender_id"
   * This ID is used to identify the sender of Firebase Cloud Messages.
   */
  FIREBASE_MESSAGING_SENDER_ID: import.meta.env?.VITE_FIREBASE_MESSAGING_SENDER_ID,

  /**
   * Firebase App ID
   * @example "your_firebase_app_id"
   * This ID uniquely identifies your Firebase app.
   */
  FIREBASE_APP_ID: import.meta.env?.VITE_FIREBASE_APP_ID,

  /**
   * Firebase Measurement ID
   * @example "your_firebase_measurement_id"
   * This ID is used for Google Analytics measurement.
   */
  FIREBASE_MEASUREMENT_ID: import.meta.env?.VITE_FIREBASE_MEASUREMENT_ID,

  /**
   * API Base URL
   * @example "http://localhost:3000/api/"
   * This URL is the base endpoint for your API requests.
   */
  API_BASE_URL: import.meta.env?.VITE_API_BASE_URL,

  /**
   * Web App URL
   * @example "http://localhost:3000"
   * This URL is the base endpoint for your web app.
   */
  WEB_APP_URL: import.meta.env?.VITE_WEB_APP_URL,

  /**
   * Sentry Organization
   * @example "your_sentry_org"
   * This variable is used to identify the Sentry organization.
   */
  SENTRY_ORG: import.meta.env?.VITE_SENTRY_ORG,

  /**
   * Sentry Project
   * @example "your_sentry_project"
   * This variable is used to identify the Sentry project.
   */
  SENTRY_PROJECT: import.meta.env?.VITE_SENTRY_PROJECT,

  /**
   * Sentry Script URL
   * @example "https://js.sentry-cdn.com/479fcfcaf3a91f7a91ab8b9fb64c7c2a.min.js"
   * This URL is the base endpoint for your Sentry script.
   */
  SENTRY_SCRIPT_URL: import.meta.env?.VITE_SENTRY_SCRIPT_URL,

  /**
   * Sentry DSN
   * @example "your_sentry_dsn"
   * This DSN (Data Source Name) is used to configure and authenticate the application with Sentry.
   */
  SENTRY_DSN: import.meta.env?.VITE_SENTRY_DSN,

  /**
   * Stripe Publishable Key
   * @example "pk_test_1234567890abcdef1234567890abcdef"
   * This key is used to authenticate requests from your app to Stripe.
   */
  STRIPE_PUBLISHABLE_KEY: import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY,

  /**
   * Enable Usersnap
   * @example "true"
   * This variable determines whether to enable Usersnap.
   */
  USERSNAP_ENABLED: import.meta.env?.VITE_USERSNAP_ENABLED,

  /**
   * Usersnap Script URL
   * @example "https://widget.usersnap.com/global/load/56cf5855-4d33-4926-839f-b2bb23d6f599?onload=onUsersnapLoad"
   * This URL is used to load the Usersnap script.
   */
  USERSNAP_SCRIPT_URL: import.meta.env?.VITE_USERSNAP_SCRIPT_URL,

  /**
   * Loyalty Program Modal Display Limit
   * @example "3"
   * This variable determines the number of times the loyalty program modal will be displayed when the user does not have a loyalty program.
   */
  LOYALTY_PROGRAM_MODAL_DISPLAY_LIMIT: import.meta.env?.VITE_LOYALTY_PROGRAM_MODAL_DISPLAY_LIMIT,

  /**
   * Passport Child Age Limit
   * @example "18"
   * This variable determines the age limit for a passenger to be considered a child.
   */
  PASSPORT_CHILD_AGE_LIMIT: import.meta.env?.VITE_PASSPORT_CHILD_AGE_LIMIT,

  /**
   * Product Name
   * @example "Hurrier"
   * This variable determines the name of the product.
   */
  PRODUCT_NAME: import.meta.env?.VITE_PRODUCT_NAME,

  /**
   * Default Page Size
   * @example "10"
   * This variable determines the default page size for pagination.
   */
  DEFAULT_PAGE_SIZE: import.meta.env?.VITE_DEFAULT_PAGE_SIZE,

  /**
   * Stripe Main Currency
   * @example "USD"
   * This variable determines the main currency for Stripe.
   */
  STRIPE_MAIN_CURRENCY: import.meta.env?.VITE_STRIPE_MAIN_CURRENCY,

  /**
   * Meta Pixel ID
   * @example "1098366315187589"
   * This ID is used for Facebook Pixel.
   */
  META_PIXEL_ID: import.meta.env?.VITE_META_PIXEL_ID,

  /**
   * Zoho SalesIQ Widget Code
   * @example "siq123abc456def789ghi0jkl1mno2pqr3stu4vwx5yz67890abcdef"
   * This code is used for Zoho SalesIQ.
   */
  ZOHO_SALES_IQ_WIDGET_CODE: import.meta.env?.VITE_ZOHO_SALES_IQ_WIDGET_CODE,

  /**
   * Google Analytics ID
   * @example "G-1234567890"
   * This ID is used for Google Analytics.
   */
  GOOGLE_ANALYTICS_ID: import.meta.env?.VITE_GOOGLE_ANALYTICS_ID,

  /**
   * API Timeout
   * @example "30000"
   * This variable determines the timeout for API requests.
   */
  API_TIMEOUT: import.meta.env?.VITE_API_TIMEOUT,

  /**
   * Logistics Partner Countries
   * @example "JM,US,CA"
   * Comma-separated list of IATA country codes for logistics partner operations.
   * Used to filter airports available for logistics partner airport selection.
   */
  LOGISTICS_PARTNER_COUNTRIES: import.meta.env?.VITE_LOGISTICS_PARTNER_COUNTRIES || 'JM',

  /**
   * Shared Trip Travel Date Offset
   * @example "1"
   * Number of days to add to the travel date to create a general arrival date estimation displayed on shared trip page.
   * This protects traveler privacy by showing estimated dates rather than exact travel dates.
   * Defaults to 1 if not specified.
   */
  SHARED_TRIP_TRAVEL_DATE_OFFSET: import.meta.env?.VITE_SHARED_TRIP_TRAVEL_DATE_OFFSET || '1',

  /**
   * USPS Location Search Radius (miles)
   * @example "10"
   * Radius in miles for finding nearby USPS drop-off locations. Used when auto-fetching locations (e.g. flight booking dropoff step).
   * Defaults to 10 if not specified.
   */
  USPS_LOCATION_SEARCH_RADIUS_MILES: import.meta.env?.VITE_USPS_LOCATION_SEARCH_RADIUS_MILES ?? '10',

  /**
   * Fulfillment order item photo upload max size (megabytes)
   * @example "10"
   * Caps Create Fulfillment Order item photo uploads. Defaults to 10 when unset or invalid (`VITE_MAX_FULFILLMENT_ITEM_PHOTO_SIZE_MB`).
   */
  MAX_FULFILLMENT_ITEM_PHOTO_SIZE_MB: resolvedMaxFulfillmentItemPhotoSizeMb
}

export default env
